/**
 * PrismHR (SL-1922).
 *
 * Fuentes: los colores globales del kit de Elementor de prismhr.com y el SVG de
 * su logo. Ver la Epic SL-1916 y el ADR-044 (marca elegida por deploy).
 *
 * Dos decisiones que no salen de la web y conviene no "corregir":
 *
 *   · La rampa de acento NO es la de su web: es el tono de PrismHR (209°) con la
 *     MISMA luminancia que cada paso de la rampa de Silia. Así cada contraste ya
 *     medido en la app se conserva — `n700` sobre blanco da ~7.0:1 en las dos.
 *   · El hover del CTA es `#007a70`, no el `#00b2a9` de su web: blanco sobre
 *     `#00b2a9` da 2.64:1 y no pasa AA. `#007a70` mantiene el azul→turquesa de
 *     su web y da 5.23:1.
 */

import type { ThemeConfig } from 'antd';
import type { BrandRamp } from '../primitives';
import { radius, typography } from '../primitives';
import { antdToken } from '../tokens';
import { buildComponents } from '../components';
import { cssVarConfig } from '../config';
import { emitAssetVars, prismhrAssets } from './assets';

const PRISMHR = {
  /** CTA. Blanco encima: 6.38:1. */
  primary: '#005eb8',
  primaryHover: '#007a70',
  primaryActive: '#005f56',
  /** Su color de texto; acá es la tinta más fuerte (13.4:1 sobre blanco). */
  ink: '#003349',
  /** Índigo del logo ("HR" y una cara del prisma). 7.25:1 con blanco. */
  indigo: '#3c539a',
  /** Su "secondary" de Elementor: el turquesa claro del agente. */
  secondary: '#00b2a9',
  teal: '#00887b',
  /** El borde suave: el de Silia, por decisión de la revisión visual. */
  hairline: '#f2f2f3',
  /** Texto sobre el turquesa del agente: 7.94:1 sobre `secondary`. */
  agentInk: '#000000',
  sky: '#6cc4e8',
  amber: '#fcb531',
  red: '#bf0d3e',
  gray: '#65676a',
  mist: '#dfe5e6',
  white: '#ffffff',
} as const;

export const PRISMHR_RAMP: BrandRamp = {
  black: PRISMHR.ink,
  lime: PRISMHR.primary,
  n100: '#ebf4fc',
  n200: '#d8e9f9',
  n400: '#a0caf1',
  n500: '#73a2ce',
  n600: '#457fb6',
  n700: '#435b73',
  n800: '#2b3e51',
  n900: '#1c2c3c',
};

/**
 * Los `--silia-*` de PrismHR. Misma regla que Traxión: las familias kind/node,
 * los shadows y los rellenos se REDECLARAN, porque un `var()` escrito en `:root`
 * queda congelado con el valor de Silia.
 */
export const PRISMHR_CSS = `
  body.template-prismhr {
    /* Brand */
    /* Decisión de marca, revisada en pantalla (2026-10-13): blanco. Distinto de
       Silia, donde es la tinta más fuerte sobre fondo claro. El texto de cuerpo
       sigue siendo navy (--silia-text, PRISMHR.ink), y también lo es la tinta
       del tema de AntD (PRISMHR_RAMP.black). */
    --silia-ink-strong: ${PRISMHR.white};
    --silia-primary: ${PRISMHR.primary};
    --silia-primary-bg-subtle: rgba(0, 94, 184, 0.08);
    --silia-primary-bg: rgba(0, 94, 184, 0.16);
    --silia-primary-border: #004a93;
    /* White on #005eb8 is 6.38:1; Silia's ink on it would be 2.10:1. */
    --silia-on-primary: ${PRISMHR.white};
    --silia-shadow-cta: 0 2px 6px rgba(0, 94, 184, 0.3);
    --silia-shadow-cta-hover: 0 4px 10px rgba(0, 122, 112, 0.35);

    /* Acento */
    --silia-accent-bg-subtle: ${PRISMHR_RAMP.n100};
    --silia-accent-bg: ${PRISMHR_RAMP.n200};
    --silia-accent-border: ${PRISMHR_RAMP.n400};
    --silia-accent-border-strong: ${PRISMHR_RAMP.n500};
    --silia-accent-fg-muted: ${PRISMHR_RAMP.n600};
    --silia-accent-fg: ${PRISMHR_RAMP.n700};
    /* La opción seleccionada va en el azul del CTA, no en el pizarra del acento
       (decisión de marca, SL-1920). */
    --silia-selected-fg: ${PRISMHR.primary};
    --silia-accent-fg-strong: ${PRISMHR_RAMP.n800};
    --silia-accent-solid: ${PRISMHR_RAMP.n900};

    /* Surfaces — neutros fríos, sobre su gris niebla. */
    --silia-bg-layout: #f5f7f8;
    --silia-bg-container: ${PRISMHR.white};
    --silia-bg-elevated: ${PRISMHR.white};
    --silia-surface-tint-1: #f7f9fa;
    --silia-surface-tint-2: #eef2f3;
    --silia-surface-tint-3: ${PRISMHR.mist};
    --silia-input-addon: #eef2f3;

    /* Text */
    --silia-text: ${PRISMHR.ink};
    --silia-text-description: ${PRISMHR.gray};
    --silia-text-icon: #8a8c8f;
    --silia-text-placeholder: #9a9c9f;
    --silia-text-inverse: ${PRISMHR.white};

    /* Borders */
    --silia-border: #c9d1d4;
    /* El borde suave es el de Silia (#f2f2f3), no su gris niebla: con #dfe5e6
       las cards y separadores se marcaban de más. --ant-colorBorderSecondary y
       el colorBorderSecondary de AntD son el mismo rol y leen este valor. */
    --silia-border-subtle: ${PRISMHR.hairline};
    --silia-fill-content: #eef2f3;
    --silia-split: #e6ebec;

    /* Semantic — de su paleta: turquesa, ámbar, rojo e índigo. */
    --silia-success-bg: #e0f3f1;
    --silia-success-fg: ${PRISMHR.teal};
    --silia-success-fg-strong: #005f56;
    --silia-success-bd: #8fd3cc;
    --silia-warning-bg: #fff4dc;
    --silia-warning-fg: #a86a00;
    --silia-warning-fg-strong: #6e4500;
    --silia-warning-bd: ${PRISMHR.amber};
    --silia-danger-bg: #fbe7ec;
    --silia-danger-fg: ${PRISMHR.red};
    --silia-danger-fg-strong: #8a0a2d;
    --silia-danger-bd: #ee9fb3;
    --silia-info-bg: #e9edf7;
    --silia-info-fg: ${PRISMHR.indigo};
    --silia-info-fg-strong: #2a3a6d;
    --silia-info-bd: #a9b6dc;

    /* Object kind. Un tono por tipo, sacado de su paleta, para que las cards de
       Home se distingan: agente turquesa (claro de #00b2a9), RTA índigo (el del
       logo), dashboard celeste, data view ámbar, QA rojo, folder neutro.
       kindContrast.test.ts vigila que no se repitan y que el texto se lea. */
    --silia-kind-folder-bg: #eef2f3;
    --silia-kind-folder-fg: #33454f;
    --silia-kind-folder-bd: #c9d1d4;
    /* El agente es el turquesa de su paleta, no el azul del CTA: un avatar no
       es un botón y no tiene que competir con él. Encima va negro: 7.94:1
       sobre #00b2a9 (blanco daba 2.64:1). */
    --silia-kind-agent-bg: ${PRISMHR.secondary};
    --silia-kind-agent-fg: ${PRISMHR.agentInk};
    --silia-kind-agent-bd: ${PRISMHR.teal};
    --silia-kind-agent-soft-bg: #e0f6f5;
    --silia-kind-agent-soft-fg: #00756f;
    --silia-kind-agent-soft-bd: #8ad9d4;
    --silia-kind-qa-bg: #fbe7ec;
    --silia-kind-qa-fg: #8a0a2d;
    --silia-kind-qa-bd: #ee9fb3;
    --silia-kind-rta-bg: #e9edf7;
    --silia-kind-rta-fg: ${PRISMHR.indigo};
    --silia-kind-rta-bd: #a9b6dc;
    --silia-kind-data-view-bg: #fff4dc;
    --silia-kind-data-view-fg: #6e4500;
    --silia-kind-data-view-bd: ${PRISMHR.amber};
    --silia-kind-dashboard-bg: #e3f4fb;
    --silia-kind-dashboard-fg: #0b5f80;
    --silia-kind-dashboard-bd: ${PRISMHR.sky};
    --silia-kind-file-bg: #eef2f3;
    --silia-kind-file-fg: ${PRISMHR.gray};
    --silia-kind-file-bd: ${PRISMHR.mist};

    /* Canvas node type */
    --silia-node-tool-bg: #e3f4fb;
    --silia-node-tool-fg: #0b5f80;
    --silia-node-tool-bd: ${PRISMHR.sky};
    --silia-node-mcp-bg: #eef2f3;
    --silia-node-mcp-fg: #3f4a50;
    --silia-node-mcp-bd: #c9d1d4;
    --silia-node-table-bg: #e0f3f1;
    --silia-node-table-fg: #005f56;
    --silia-node-table-bd: #8fd3cc;
    --silia-node-agent-bg: #fff4dc;
    --silia-node-agent-fg: #6e4500;
    --silia-node-agent-bd: ${PRISMHR.amber};

    /* Banner de bienvenida de Home: su azul, índigo, celeste y turquesa sobre su
       rampa (n200 → n400 → n600), con un brillo blanco donde Silia tenía lima. */
    --silia-welcome-artwork:
      radial-gradient(ellipse 60% 80% at 30% 100%, rgba(0, 94, 184, 0.55) 0%, transparent 70%),
      radial-gradient(ellipse 70% 80% at 70% 100%, rgba(60, 83, 154, 0.45) 0%, transparent 70%),
      radial-gradient(ellipse 60% 50% at 20% 0%, rgba(108, 196, 232, 0.6) 0%, transparent 65%),
      radial-gradient(ellipse 40% 50% at 85% 5%, rgba(0, 178, 169, 0.45) 0%, transparent 70%),
      radial-gradient(circle 100px at 75% 30%, rgba(255, 255, 255, 0.35) 0%, transparent 80%),
      linear-gradient(180deg, ${PRISMHR_RAMP.n200} 0%, ${PRISMHR_RAMP.n400} 60%, ${PRISMHR_RAMP.n600} 100%);
    --silia-welcome-text-shadow: 0 1px 8px rgba(0, 51, 73, 0.3);

    /* Texto sobre las imágenes de fondo. Mientras PrismHR use los fondos
       oscuros de Silia (prismhrAssets.placeholder) van las cremas de Silia;
       con su fondo claro (SL-1920, tarea 8) pasan a navy (PRISMHR.ink). */
    --silia-on-background: #ececdb;
    --silia-on-modules-background: #f7f7f6;

    /* Rellenos translúcidos — el negro y el acento de PrismHR, no los de Silia. */
    --silia-fill-1: rgba(0, 51, 73, 0.02);
    --silia-fill-2: rgba(0, 51, 73, 0.06);
    --silia-fill-3: rgba(0, 51, 73, 0.1);
    --silia-fill-brand-1: rgba(0, 94, 184, 0.06);
    --silia-fill-brand-2: rgba(0, 94, 184, 0.12);
    --silia-fill-inverse-1: rgba(255, 255, 255, 0.08);
    --silia-fill-inverse-2: rgba(255, 255, 255, 0.16);

    /* Elevation — sombras con matiz navy, no violeta. */
    --silia-shadow-sm: 0 2px 4px rgba(0, 51, 73, 0.03), 0 1px 6px rgba(0, 51, 73, 0.03), 0 1px 2px rgba(0, 51, 73, 0.04);
    --silia-shadow-md: 0 6px 16px rgba(0, 51, 73, 0.07), 0 3px 6px rgba(0, 51, 73, 0.05);
    --silia-shadow-lg: 0 12px 32px rgba(0, 51, 73, 0.12), 0 4px 8px rgba(0, 51, 73, 0.06);
    --silia-shadow-xl: 0 18px 50px rgba(0, 51, 73, 0.3);
    --silia-focus-ring: 0 0 0 3px ${PRISMHR_RAMP.n200};
    --silia-shadow-glow: 0 0 0 3px rgba(0, 94, 184, 0.16);

    /* Tipografía y forma — las de Silia, DECLARADAS a propósito: no hay manual
       de PrismHR que diga otra cosa todavía, y heredarlas en silencio es justo
       lo que brandCompleteness no deja pasar. Cambian cuando llegue el kit. */
    --silia-font-sans: ${typography.fontSans};
    --silia-radius-xs: ${radius.xs}px;
    --silia-radius-sm: ${radius.sm}px;
    --silia-radius-md: ${radius.md}px;
    --silia-radius-lg: ${radius.lg}px;

${emitAssetVars(prismhrAssets)}

    background: #f5f7f8;
    color: ${PRISMHR.ink};
  }
`;

const PRISMHR_BASE = buildComponents(PRISMHR_RAMP);

export const PRISMHR_ANTD: ThemeConfig = {
  token: {
    ...antdToken,
    colorPrimary: PRISMHR.primary,
    colorBgLayout: '#f5f7f8',
    colorBgContainer: PRISMHR.white,
    colorBgElevated: PRISMHR.white,
    colorText: PRISMHR.ink,
    colorTextDescription: PRISMHR.gray,
    colorTextSecondary: PRISMHR.gray,
    colorTextTertiary: '#8a8c8f',
    colorTextLightSolid: PRISMHR.white,
    colorBorder: '#c9d1d4',
    colorBorderSecondary: PRISMHR.hairline,
    colorFillContent: '#eef2f3',
    colorSplit: '#e6ebec',
    colorSuccess: PRISMHR.teal,
    colorWarning: PRISMHR.amber,
    colorError: PRISMHR.red,
    colorInfo: PRISMHR.indigo,
    // Fijados como en el tema base: sin esto AntD deriva el panel desde
    // `colorInfo` y le gana al valor explícito del componente.
    colorInfoBg: '#e9edf7',
    colorInfoBorder: '#a9b6dc',
  },
  components: {
    ...PRISMHR_BASE,
    Button: {
      ...PRISMHR_BASE.Button,
      // `buildComponents` pone la tinta (texto oscuro) sobre el CTA, que es lo
      // correcto para el lima de Silia. Sobre el azul de PrismHR va blanco.
      colorTextLightSolid: PRISMHR.white,
      colorPrimaryHover: PRISMHR.primaryHover,
      colorPrimaryActive: PRISMHR.primaryActive,
    },
    Layout: {
      ...PRISMHR_BASE.Layout,
      colorBgLayout: '#f5f7f8',
      bodyBg: '#f5f7f8',
      siderBg: PRISMHR.white,
    },
  },
  cssVar: cssVarConfig,
  hashed: false,
};
